import { useEffect, useMemo, useState, type CSSProperties, type ReactNode } from "react";
import { clearLocalData, loadAppData, loadNavigationData } from "./lib/cache";
import { buildRouteToShop, getShopPoint } from "./lib/navigation";
import { filterByCategory, createShopSearch, searchShops } from "./lib/search";
import { filterFoodShops, pickRandomFood, uniqueCuisines, type FoodPickerFilters } from "./lib/randomizer";
import { formatIssueReport, type IssueType } from "./lib/issueReport";
import type { AppData, Category, Facility, NavigationData, RouteResult, Shop, ShopMapPoint } from "./types";

type Route =
  | { name: "home" }
  | { name: "search"; query: string }
  | { name: "category"; slug: string }
  | { name: "shop"; slug: string }
  | { name: "directions"; slug: string }
  | { name: "map"; floorId: string; shopSlug?: string }
  | { name: "food-picker" }
  | { name: "about-data" };

interface IssueDraft {
  shop: Shop | null;
  routeResult: RouteResult | null;
  floorId: string | null;
  defaultType: IssueType;
}

interface SchematicShop {
  point: ShopMapPoint;
  shop: Shop;
  box: { x: number; y: number; width: number; height: number };
  side: "top" | "bottom";
}

interface SchematicLandmark {
  id: string;
  name: string;
  type: string;
  x: number;
  y: number;
}

interface SchematicSegment {
  wing: string;
  label: string;
  startX: number;
  endX: number;
}

interface FloorSchematic {
  width: number;
  height: number;
  walkwayY: number;
  layoutKind: "full" | "compact" | "parking";
  centreWalkX: number;
  start: { x: number; y: number };
  liftStart: { x: number; y: number };
  shops: SchematicShop[];
  landmarks: SchematicLandmark[];
  segments: SchematicSegment[];
}

interface FloorLayoutConfig {
  minWidth: number;
  height: number;
  walkwayY: number;
  minSegmentWidth: number;
  layoutKind: FloorSchematic["layoutKind"];
}

const SCHEMATIC_CELL_WIDTH = 118;
const SCHEMATIC_CELL_HEIGHT = 52;
const SCHEMATIC_CELL_GAP = 10;
const SCHEMATIC_WALKWAY_Y = 430;

function parseRoute(): Route {
  const path = window.location.pathname;
  const search = new URLSearchParams(window.location.search);
  const parts = path.split("/").filter(Boolean);

  if (parts[0] === "search") return { name: "search", query: search.get("q") ?? "" };
  if (parts[0] === "category" && parts[1]) return { name: "category", slug: parts[1] };
  if (parts[0] === "shop" && parts[1]) return { name: "shop", slug: parts[1] };
  if (parts[0] === "directions" && parts[1]) return { name: "directions", slug: parts[1] };
  if (parts[0] === "map" && parts[1]) return { name: "map", floorId: parts[1], shopSlug: parts[3] };
  if (parts[0] === "food-picker") return { name: "food-picker" };
  if (parts[0] === "about-data") return { name: "about-data" };
  return { name: "home" };
}

function isLocalRuntime(): boolean {
  const hostname = window.location.hostname;
  return hostname === "localhost" || hostname === "127.0.0.1" || hostname === "0.0.0.0";
}

function categoryName(categoryId: string, categories: Category[]): string {
  return categories.find((category) => category.id === categoryId)?.name ?? categoryId;
}

function shortName(name: string): string {
  return name.length > 18 ? `${name.slice(0, 16)}...` : name;
}

function mapName(name: string): string {
  return name.length > 14 ? `${name.slice(0, 12)}...` : name;
}

function mapLot(locationCode: string): string {
  return locationCode.length > 15 ? `${locationCode.slice(0, 13)}...` : locationCode;
}

function floorIdFromShop(shop: Shop): string {
  return normalizeFloorId(shop.floor);
}

function normalizeFloorId(floor: string): string {
  const value = floor.trim().toLowerCase();
  if (value === "p1") return "b1";
  if (value === "p2") return "b2";
  if (value === "p3") return "b3";
  return value || "unknown";
}

function compareShopByLot(a: Shop, b: Shop): number {
  return a.lotSortKey.localeCompare(b.lotSortKey) || a.name.localeCompare(b.name);
}

function categoryVisual(categoryId: string | undefined): { accent: string; fill: string; text: string } {
  const visuals: Record<string, { accent: string; fill: string; text: string }> = {
    "food-beverage": { accent: "#0f766e", fill: "#cfeee7", text: "#083f3a" },
    anchor: { accent: "#b7791f", fill: "#ffe4a6", text: "#563600" },
    supermarket: { accent: "#6f9d32", fill: "#e4f5c8", text: "#2f4b0e" },
    "health-beauty": { accent: "#e0523f", fill: "#ffd9d2", text: "#671a10" },
    fashion: { accent: "#c04a7a", fill: "#ffdce8", text: "#631536" },
    "footwear-bags-accessories": { accent: "#4077a8", fill: "#dceeff", text: "#173f62" },
    "timepiece-jewellery": { accent: "#49545a", fill: "#e5eaec", text: "#202124" },
    "optical-eyewear": { accent: "#16839a", fill: "#d8f4f7", text: "#0a4a56" },
    "digital-lifestyle": { accent: "#0f6996", fill: "#d9effc", text: "#0b3d57" },
    entertainment: { accent: "#7654a6", fill: "#eadfff", text: "#3b2366" },
    edutainment: { accent: "#6f9d32", fill: "#e9f7ce", text: "#2f4b0e" },
    services: { accent: "#4077a8", fill: "#dfeefa", text: "#173f62" },
    "home-living": { accent: "#2b7a68", fill: "#dff2ec", text: "#173f37" },
    "gifts-souvenirs": { accent: "#c04a7a", fill: "#ffdce8", text: "#631536" },
    florist: { accent: "#e0523f", fill: "#ffe4df", text: "#671a10" },
    "children-maternity": { accent: "#b7791f", fill: "#fff1c7", text: "#563600" },
    facilities: { accent: "#6f9d32", fill: "#ecf7d5", text: "#2f4b0e" },
    parking: { accent: "#49545a", fill: "#eceff1", text: "#202124" }
  };
  return visuals[categoryId ?? ""] ?? { accent: "#69716d", fill: "#eef1ef", text: "#202124" };
}

function landmarkSymbol(type: string): string {
  if (type === "toilet") return "WC";
  if (type === "surau") return "S";
  if (type === "lift") return "L";
  if (type === "escalator") return "E";
  if (type === "parking") return "P";
  if (type === "information") return "i";
  if (type === "entrance") return "IN";
  return ".";
}

function readableType(value: string): string {
  return value
    .replace(/[-_]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function wingLabel(wing: string | null): string {
  if (wing === "west") return "West / Tower Two side";
  if (wing === "east") return "East / Symphony side";
  if (wing === "centre") return "Centre walk";
  return "Needs map review";
}

function coverageLabel(status: AppData["manifest"]["coverageStatus"]): string {
  if (status === "complete-reviewed") return "Complete reviewed";
  if (status === "map-provider-visible-pois") return "Provider full visible POIs";
  return "Partial seed";
}

function mapStyle(categoryId: string | undefined): CSSProperties {
  const visual = categoryVisual(categoryId);
  return {
    "--shop-accent": visual.accent,
    "--shop-fill": visual.fill,
    "--shop-text": visual.text
  } as CSSProperties;
}

function rowsPerSideFor(count: number): number {
  if (count > 120) return 4;
  if (count > 64) return 3;
  return 2;
}

function floorLayoutConfig(floorId: string): FloorLayoutConfig {
  if (floorId === "lg") {
    return { minWidth: 2200, height: 900, walkwayY: SCHEMATIC_WALKWAY_Y, minSegmentWidth: 680, layoutKind: "full" };
  }
  if (floorId === "g" || floorId === "l1" || floorId === "l2") {
    return { minWidth: 1900, height: 860, walkwayY: 420, minSegmentWidth: 620, layoutKind: "full" };
  }
  if (floorId === "l3" || floorId === "l4") {
    return { minWidth: floorId === "l4" ? 1120 : 1280, height: floorId === "l4" ? 600 : 680, walkwayY: floorId === "l4" ? 300 : 330, minSegmentWidth: 460, layoutKind: "compact" };
  }
  return { minWidth: 1180, height: 620, walkwayY: 310, minSegmentWidth: 460, layoutKind: "parking" };
}

function buildFloorSchematic(
  floorId: string,
  floorPoints: ShopMapPoint[],
  shopsById: Map<string, Shop>,
  floorLandmarks: NavigationData["landmarks"]
): FloorSchematic {
  const layout = floorLayoutConfig(floorId);
  const groupOrder = ["west", "centre", "east", "unknown"];
  const pointsWithShops = floorPoints
    .map((point) => {
      const shop = shopsById.get(point.shopId);
      return shop ? { point, shop } : null;
    })
    .filter((item): item is { point: ShopMapPoint; shop: Shop } => Boolean(item))
    .sort((a, b) => compareShopByLot(a.shop, b.shop));

  const plotted: SchematicShop[] = [];
  const segments: SchematicSegment[] = [];
  let cursorX = 138;

  for (const wing of groupOrder) {
    const group = pointsWithShops.filter(({ shop, point }) => (shop.wing ?? point.wing ?? "unknown") === wing);
    if (group.length === 0) continue;

    const rowsPerSide = rowsPerSideFor(group.length);
    const rowCapacity = Math.max(1, Math.ceil(group.length / (rowsPerSide * 2)));
    const segmentWidth = Math.max(layout.minSegmentWidth, rowCapacity * (SCHEMATIC_CELL_WIDTH + SCHEMATIC_CELL_GAP) + 70);
    const segmentStart = cursorX;
    const segmentEnd = segmentStart + segmentWidth;
    segments.push({ wing, label: wingLabel(wing === "unknown" ? null : wing), startX: segmentStart, endX: segmentEnd });

    group.forEach(({ point, shop }, index) => {
      const laneIndex = Math.floor(index / rowCapacity);
      const laneSlot = laneIndex % (rowsPerSide * 2);
      const row = laneSlot % rowsPerSide;
      const side: "top" | "bottom" = laneSlot < rowsPerSide ? "top" : "bottom";
      const col = index % rowCapacity;
      const x = segmentStart + 32 + col * (SCHEMATIC_CELL_WIDTH + SCHEMATIC_CELL_GAP);
      const y =
        side === "top"
          ? layout.walkwayY - 96 - (rowsPerSide - 1 - row) * (SCHEMATIC_CELL_HEIGHT + SCHEMATIC_CELL_GAP)
          : layout.walkwayY + 96 + row * (SCHEMATIC_CELL_HEIGHT + SCHEMATIC_CELL_GAP);

      plotted.push({
        point,
        shop,
        side,
        box: { x, y, width: SCHEMATIC_CELL_WIDTH, height: SCHEMATIC_CELL_HEIGHT }
      });
    });

    cursorX = segmentEnd + 52;
  }

  const width = Math.max(layout.minWidth, cursorX + 160);
  const height = layout.height;
  const start = { x: 260, y: layout.walkwayY };
  const liftStart = { x: layout.layoutKind === "full" ? 380 : 300, y: layout.walkwayY };
  const centreSegment = segments.find((segment) => segment.wing === "centre");
  const centreWalkX = centreSegment ? (centreSegment.startX + centreSegment.endX) / 2 : Math.round(width / 2);
  const sortedLandmarks = floorLandmarks
    .slice()
    .sort((a, b) => a.type.localeCompare(b.type) || a.name.localeCompare(b.name))
    .slice(0, 36);
  const landmarkColumns = Math.max(6, Math.ceil(sortedLandmarks.length / 3));
  const landmarkGap = Math.max(82, (width - 720) / Math.max(1, landmarkColumns - 1));
  const landmarks = sortedLandmarks.map((landmark, index) => {
    const column = Math.floor(index / 3);
    const row = index % 3;
    return {
      id: landmark.id,
      name: landmark.name,
      type: landmark.type,
      x: 420 + column * landmarkGap,
      y: layout.walkwayY + [-42, 0, 42][row]
    };
  });

  return {
    width,
    height,
    walkwayY: layout.walkwayY,
    layoutKind: layout.layoutKind,
    centreWalkX,
    start,
    liftStart,
    shops: plotted,
    landmarks,
    segments
  };
}

function mallShellPath(schematic: FloorSchematic): string {
  const { width, height, layoutKind } = schematic;
  if (layoutKind === "compact") {
    return `M104 108 H${width - 190} L${width - 118} 172 V${height - 122} H130 L76 ${height - 190} V158 Z`;
  }
  if (layoutKind === "parking") {
    return `M96 132 H${width - 148} V${height - 130} H122 L80 ${height - 186} V182 Z`;
  }
  return `M80 118 H${width - 180} L${width - 104} 214 V690 L${width - 218} 790 H118 L72 724 V182 Z`;
}

function walkwayPaths(schematic: FloorSchematic): { main: string; branches: string[] } {
  const { width, height, walkwayY, layoutKind, centreWalkX } = schematic;
  if (layoutKind === "compact") {
    return {
      main: `M150 ${walkwayY} H${width - 178}`,
      branches: [
        `M300 ${walkwayY} V${Math.max(142, walkwayY - 112)} H${width - 380}`,
        `M${width - 430} ${walkwayY} V${Math.min(height - 132, walkwayY + 92)} H${width - 250}`
      ]
    };
  }
  if (layoutKind === "parking") {
    return {
      main: `M150 ${walkwayY} H${width - 160}`,
      branches: [
        `M300 ${walkwayY} V${Math.min(height - 112, walkwayY + 126)}`,
        `M${width - 360} ${walkwayY} V${Math.max(142, walkwayY - 92)}`
      ]
    };
  }
  return {
    main: `M180 ${walkwayY} H${width - 220}`,
    branches: [
      `M260 ${walkwayY} V160 H${width - 300}`,
      `M260 ${walkwayY} V728 H${width - 420}`,
      `M${Math.round(centreWalkX)} ${walkwayY} V${walkwayY - 126}`,
      `M${width - 560} ${walkwayY} V${walkwayY + 126}`
    ]
  };
}

function routePolyline(
  selected: SchematicShop | null,
  schematic: FloorSchematic,
  selectedFloorId: string
): { points: string; coords: Array<[number, number]> } | null {
  if (!selected) return null;
  const targetX = selected.box.x + selected.box.width / 2;
  const start = selectedFloorId === "lg" ? schematic.start : schematic.liftStart;
  const doorwayY = selected.side === "top" ? selected.box.y + selected.box.height + 6 : selected.box.y - 6;
  const coords: Array<[number, number]> = [
    [start.x, start.y],
    [targetX, schematic.walkwayY],
    [targetX, doorwayY]
  ];

  return {
    points: coords.map(([x, y]) => `${Math.round(x)},${Math.round(y)}`).join(" "),
    coords
  };
}

function navigateTo(path: string, setRoute: (route: Route) => void): void {
  window.history.pushState({}, "", path);
  setRoute(parseRoute());
  window.scrollTo({ top: 0, behavior: "smooth" });
}

export default function App() {
  const [route, setRoute] = useState<Route>(() => parseRoute());
  const [appData, setAppData] = useState<AppData | null>(null);
  const [navigationData, setNavigationData] = useState<NavigationData | null>(null);
  const [loadingError, setLoadingError] = useState<string | null>(null);
  const [issueDraft, setIssueDraft] = useState<IssueDraft | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    const handler = () => setRoute(parseRoute());
    window.addEventListener("popstate", handler);
    return () => window.removeEventListener("popstate", handler);
  }, []);

  useEffect(() => {
    loadAppData()
      .then((data) => {
        setAppData(data);
        return loadNavigationData(data.manifest);
      })
      .then(setNavigationData)
      .catch((error: Error) => setLoadingError(error.message));
  }, []);

  useEffect(() => {
    if (!toast) return;
    const timeout = window.setTimeout(() => setToast(null), 2600);
    return () => window.clearTimeout(timeout);
  }, [toast]);

  const navigate = (path: string) => navigateTo(path, setRoute);

  const activeShop = useMemo(() => {
    if (!appData) return null;
    const slug =
      route.name === "shop" || route.name === "directions"
        ? route.slug
        : route.name === "map"
          ? route.shopSlug
          : null;
    return slug ? appData.shops.find((shop) => shop.slug === slug) ?? null : null;
  }, [appData, route]);

  if (loadingError) {
    return (
      <Shell route={route} navigate={navigate} onReport={() => setIssueDraft(defaultIssue(null))}>
        <ErrorState message={loadingError} onReport={() => setIssueDraft(defaultIssue(null))} />
      </Shell>
    );
  }

  if (!appData) {
    return <LoadingScreen />;
  }

  return (
    <Shell route={route} navigate={navigate} onReport={() => setIssueDraft(defaultIssue(activeShop))}>
      {renderRoute({
        route,
        appData,
        navigationData,
        navigate,
        openIssue: setIssueDraft,
        setToast
      })}

      {issueDraft && (
        <IssueReportModal
          appData={appData}
          draft={issueDraft}
          routePath={window.location.pathname + window.location.search}
          onClose={() => setIssueDraft(null)}
          onCopied={() => setToast("Issue report copied")}
        />
      )}

      {toast && <div className="toast">{toast}</div>}
    </Shell>
  );
}

function renderRoute(args: {
  route: Route;
  appData: AppData;
  navigationData: NavigationData | null;
  navigate: (path: string) => void;
  openIssue: (draft: IssueDraft) => void;
  setToast: (message: string) => void;
}) {
  const { route, appData, navigationData, navigate, openIssue, setToast } = args;

  if (route.name === "search") {
    return <SearchView appData={appData} initialQuery={route.query} navigate={navigate} />;
  }

  if (route.name === "category") {
    const category = appData.categories.find((item) => item.slug === route.slug);
    return <CategoryView appData={appData} category={category ?? null} navigate={navigate} />;
  }

  if (route.name === "shop") {
    const shop = appData.shops.find((item) => item.slug === route.slug) ?? null;
    return (
      <ShopDetail
        appData={appData}
        navigationData={navigationData}
        shop={shop}
        navigate={navigate}
        openIssue={openIssue}
      />
    );
  }

  if (route.name === "directions") {
    const shop = appData.shops.find((item) => item.slug === route.slug) ?? null;
    return (
      <DirectionsView
        appData={appData}
        navigationData={navigationData}
        shop={shop}
        navigate={navigate}
        openIssue={openIssue}
      />
    );
  }

  if (route.name === "map") {
    const shop = route.shopSlug ? appData.shops.find((item) => item.slug === route.shopSlug) ?? null : null;
    return (
      <MapView
        appData={appData}
        navigationData={navigationData}
        floorId={route.floorId}
        selectedShop={shop}
        navigate={navigate}
        openIssue={openIssue}
      />
    );
  }

  if (route.name === "food-picker") {
    return <FoodPickerView appData={appData} navigate={navigate} />;
  }

  if (route.name === "about-data" && isLocalRuntime()) {
    return <AboutDataView appData={appData} setToast={setToast} />;
  }

  return <HomeView navigate={navigate} />;
}

function defaultIssue(shop: Shop | null): IssueDraft {
  return {
    shop,
    routeResult: null,
    floorId: shop ? floorIdFromShop(shop) : null,
    defaultType: shop ? "Wrong shop info" : "Other"
  };
}

function Shell({
  route,
  navigate,
  onReport,
  children
}: {
  route: Route;
  navigate: (path: string) => void;
  onReport: () => void;
  children: ReactNode;
}) {
  const navItems = [
    { label: "Find", path: "/", active: route.name === "home" || route.name === "search" },
    { label: "Browse", path: "/category/food-beverage", active: route.name === "category" },
    { label: "Map", path: "/map/lg", active: route.name === "map" || route.name === "directions" },
    { label: "Random", path: "/food-picker", active: route.name === "food-picker" },
    isLocalRuntime() ? { label: "Data", path: "/about-data", active: route.name === "about-data" } : null
  ].filter((item): item is { label: string; path: string; active: boolean } => Boolean(item));

  return (
    <div className="app-shell">
      <header className="topbar">
        <button className="brand" onClick={() => navigate("/")} aria-label="Go to home">
          <span className="brand-mark">M</span>
          <span>
            <strong>MakanMane</strong>
            <small>IOI City Mall Putrajaya</small>
          </span>
        </button>
        <button className="ghost-button" onClick={onReport}>
          Report
        </button>
      </header>

      <main>{children}</main>

      <nav className="bottom-nav" aria-label="Main navigation" style={{ "--nav-count": navItems.length } as CSSProperties}>
        {navItems.map((item) => (
          <button
            key={item.path}
            className={item.active ? "active" : ""}
            onClick={() => navigate(item.path)}
            aria-current={item.active ? "page" : undefined}
          >
            {item.label}
          </button>
        ))}
      </nav>
    </div>
  );
}

function LoadingScreen() {
  return (
    <div className="loading-screen">
      <div className="loader-card">
        <span className="brand-mark">M</span>
        <strong>Loading MakanMane</strong>
        <small>Preparing cached shop data and indoor map.</small>
      </div>
    </div>
  );
}

function ErrorState({ message, onReport }: { message: string; onReport: () => void }) {
  return (
    <section className="panel state-panel">
      <h1>Data could not load</h1>
      <p>{message}</p>
      <button className="primary-button" onClick={onReport}>
        Report issue
      </button>
    </section>
  );
}

function HomeView({ navigate }: { navigate: (path: string) => void }) {
  const [query, setQuery] = useState("");

  return (
    <div className="page-stack">
      <section className="finder-hero">
        <form
          className="search-form"
          onSubmit={(event) => {
            event.preventDefault();
            navigate(`/search?q=${encodeURIComponent(query)}`);
          }}
        >
          <label htmlFor="home-search">Search by shop, food, floor, or lot</label>
          <div className="search-row">
            <input
              id="home-search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Try A&W, pharmacy, L2, coffee"
            />
            <button className="primary-button" type="submit">
              Search
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}

function SearchView({
  appData,
  initialQuery,
  navigate
}: {
  appData: AppData;
  initialQuery: string;
  navigate: (path: string) => void;
}) {
  const [query, setQuery] = useState(initialQuery);
  const searcher = useMemo(() => createShopSearch(appData.shops, appData.categories), [appData]);
  const results = useMemo(() => searchShops(searcher, appData.shops, query), [appData.shops, query, searcher]);

  return (
    <div className="page-stack">
      <PageHeader eyebrow="Find" title="Search" />
      <form
        className="search-form compact"
        onSubmit={(event) => {
          event.preventDefault();
          navigate(`/search?q=${encodeURIComponent(query)}`);
        }}
      >
        <label htmlFor="search-page-input">Search</label>
        <div className="search-row">
          <input id="search-page-input" value={query} onChange={(event) => setQuery(event.target.value)} autoFocus />
          <button className="primary-button" type="submit">
            Go
          </button>
        </div>
      </form>
      {results.length > 0 ? (
        <ShopList shops={results} categories={appData.categories} navigate={navigate} />
      ) : (
        <EmptyState
          title="No matching shop yet"
          message="Try a category, floor code, or shorter keyword."
          actionLabel="Browse"
          onAction={() => navigate("/category/food-beverage")}
        />
      )}
    </div>
  );
}

function CategoryView({
  appData,
  category,
  navigate
}: {
  appData: AppData;
  category: Category | null;
  navigate: (path: string) => void;
}) {
  const shops = category ? filterByCategory(appData.shops, category.id).sort(compareShopByLot) : [];

  return (
    <div className="page-stack">
      <PageHeader eyebrow="Browse" title="Shops" />
      <section className="section">
        <div className="chip-grid">
          {appData.categories.map((item) => (
            <button
              key={item.id}
              className={category?.id === item.id ? `category-chip ${item.color} active` : `category-chip ${item.color}`}
              onClick={() => navigate(`/category/${item.slug}`)}
            >
              {item.name}
            </button>
          ))}
        </div>
      </section>
      {category ? (
        <section className="section">
          <div className="section-header">
            <h2>{category.name}</h2>
          </div>
          {shops.length > 0 ? (
            <ShopList shops={shops} categories={appData.categories} navigate={navigate} />
          ) : (
            <EmptyState title="No shops yet" message="Try another category." />
          )}
        </section>
      ) : (
        <EmptyState title="Category not found" message="Choose another category." />
      )}
    </div>
  );
}

function ShopDetail({
  appData,
  navigationData,
  shop,
  navigate,
  openIssue
}: {
  appData: AppData;
  navigationData: NavigationData | null;
  shop: Shop | null;
  navigate: (path: string) => void;
  openIssue: (draft: IssueDraft) => void;
}) {
  if (!shop) return <EmptyState title="Shop not found" message="Search again or browse a category." />;

  const point = navigationData ? getShopPoint(shop, navigationData) : null;

  return (
    <div className="page-stack">
      <section className="shop-detail">
        <div>
          <p className="eyebrow">{shop.locationCode}</p>
          <h1>{shop.name}</h1>
          <p>{shop.directionHint}</p>
        </div>
        <div className="detail-actions">
          <button className="primary-button" onClick={() => navigate(`/directions/${shop.slug}`)}>
            Navigate
          </button>
          <button className="secondary-button" onClick={() => navigate(`/map/${floorIdFromShop(shop)}/shop/${shop.slug}`)}>
            View map
          </button>
          <button className="ghost-button" onClick={() => openIssue(defaultIssue(shop))}>
            Report
          </button>
        </div>
      </section>

      <section className="info-grid">
        <InfoTile label="Category" value={shop.categoryIds.map((id) => categoryName(id, appData.categories)).join(", ")} />
        <InfoTile label="Floor" value={shop.floor} />
        <InfoTile label="Lot" value={shop.lot} />
        <InfoTile label="Map data" value={point?.dataQuality ?? "unknown"} />
        <InfoTile label="Halal" value={shop.halalStatus.replace("-", " ")} />
        <InfoTile label="Budget" value={shop.budgetLevel} />
      </section>

      <section className="panel">
        <h2>Route note</h2>
        <p>{shop.nearestLandmark ? `Nearest landmark: ${shop.nearestLandmark}. ` : ""}{shop.halalNote}</p>
      </section>
    </div>
  );
}

function DirectionsView({
  appData,
  navigationData,
  shop,
  navigate,
  openIssue
}: {
  appData: AppData;
  navigationData: NavigationData | null;
  shop: Shop | null;
  navigate: (path: string) => void;
  openIssue: (draft: IssueDraft) => void;
}) {
  if (!shop) return <EmptyState title="Shop not found" message="Search again or browse a category." />;
  if (!navigationData) return <EmptyState title="Map data loading" message="Try again in a moment." />;

  const floorId = floorIdFromShop(shop);
  const route = buildRouteToShop(shop, navigationData);

  return (
    <div className="page-stack">
      <PageHeader eyebrow="Directions" title={`Route to ${shop.name}`} />
      <section className="route-summary">
        <InfoTile label="Start" value="LG Tower Two entrance" />
        <InfoTile label="Destination" value={`${shop.locationCode} (${shop.floor})`} />
        <InfoTile label="Route status" value={route.status.replace(/-/g, " ")} />
      </section>

      <IndoorMap
        appData={appData}
        navigationData={navigationData}
        selectedFloorId={floorId}
        selectedShop={shop}
        routeResult={route}
        navigate={navigate}
      />

      <section className="panel">
        <div className="section-header">
          <h2>Steps</h2>
          <button
            className="ghost-button"
            onClick={() =>
              openIssue({
                shop,
                routeResult: route,
                floorId,
                defaultType: "Navigation instruction unclear"
              })
            }
          >
            Report
          </button>
        </div>
        <ol className="steps">
          {route.steps.map((step) => (
            <li key={step}>{step}</li>
          ))}
        </ol>
      </section>
    </div>
  );
}

function MapView({
  appData,
  navigationData,
  floorId,
  selectedShop,
  navigate,
  openIssue
}: {
  appData: AppData;
  navigationData: NavigationData | null;
  floorId: string;
  selectedShop: Shop | null;
  navigate: (path: string) => void;
  openIssue: (draft: IssueDraft) => void;
}) {
  if (!navigationData) return <EmptyState title="Map data loading" message="Try again in a moment." />;
  const routeResult = selectedShop ? buildRouteToShop(selectedShop, navigationData) : null;

  return (
    <div className="page-stack">
      <PageHeader eyebrow="Indoor map" title="IOI-layout-style route guide" />
      <FloorTabs floors={navigationData.floors} currentFloorId={floorId} navigate={navigate} />
      <IndoorMap
        appData={appData}
        navigationData={navigationData}
        selectedFloorId={floorId}
        selectedShop={selectedShop}
        routeResult={routeResult}
        navigate={navigate}
      />
      <section className="panel">
        <div className="section-header">
          <h2>{selectedShop ? selectedShop.name : "Map notes"}</h2>
          <button
            className="ghost-button"
            onClick={() =>
              openIssue({
                shop: selectedShop,
                routeResult,
                floorId,
                defaultType: selectedShop ? "Wrong lot/location" : "Other"
              })
            }
          >
            Report
          </button>
        </div>
        <p>
          Start marker is fixed at the LG Tower Two entrance. Use the floor tabs when you already know
          the level you have reached.
        </p>
      </section>
    </div>
  );
}

function FoodPickerView({ appData, navigate }: { appData: AppData; navigate: (path: string) => void }) {
  const foodShops = appData.shops.filter((shop) => shop.categoryIds.includes("food-beverage"));
  const cuisines = uniqueCuisines(foodShops);
  const floors = Array.from(new Set(foodShops.map((shop) => shop.floor))).sort();
  const [filters, setFilters] = useState<FoodPickerFilters>({
    halal: "any",
    cuisine: "any",
    budget: "any",
    floor: "any"
  });
  const [picked, setPicked] = useState<Shop | null>(null);
  const eligible = filterFoodShops(foodShops, filters);

  const reroll = () => setPicked(pickRandomFood(eligible, picked?.id ?? null));

  return (
    <div className="page-stack">
      <PageHeader eyebrow="Random" title="Pick a place to eat." />
      <section className="panel filter-panel">
        <Select
          label="Halal"
          value={filters.halal}
          onChange={(value) => setFilters({ ...filters, halal: value as FoodPickerFilters["halal"] })}
          options={[
            ["any", "Any"],
            ["halal-certified", "Halal certified"],
            ["halal-friendly", "Halal friendly"],
            ["unknown", "Unknown"]
          ]}
        />
        <Select
          label="Cuisine"
          value={filters.cuisine}
          onChange={(value) => setFilters({ ...filters, cuisine: value })}
          options={[["any", "Any"], ...cuisines.map((cuisine) => [cuisine, cuisine] as [string, string])]}
        />
        <Select
          label="Budget"
          value={filters.budget}
          onChange={(value) => setFilters({ ...filters, budget: value as FoodPickerFilters["budget"] })}
          options={[
            ["any", "Any"],
            ["low", "Low"],
            ["medium", "Medium"],
            ["high", "High"]
          ]}
        />
        <Select
          label="Floor"
          value={filters.floor}
          onChange={(value) => setFilters({ ...filters, floor: value })}
          options={[["any", "Any"], ...floors.map((floor) => [floor, floor] as [string, string])]}
        />
        <button className="primary-button" onClick={reroll}>
          Pick for me
        </button>
      </section>

      {picked ? (
        <section className="picked-card">
          <p className="eyebrow">Matched {eligible.length} option{eligible.length === 1 ? "" : "s"}</p>
          <h2>{picked.name}</h2>
          <p>
            {picked.locationCode} · {picked.cuisineTypes.join(", ") || "Food"} · {picked.halalStatus.replace("-", " ")}
          </p>
          <div className="detail-actions">
            <button className="primary-button" onClick={() => navigate(`/directions/${picked.slug}`)}>
              Navigate
            </button>
            <button className="secondary-button" onClick={reroll}>
              Reroll
            </button>
          </div>
        </section>
      ) : (
        <EmptyState
          title={eligible.length ? "Ready to pick" : "No match for those filters"}
          message={eligible.length ? `${eligible.length} places match the current filters.` : "Relax one filter and try again."}
        />
      )}
    </div>
  );
}

function AboutDataView({ appData, setToast }: { appData: AppData; setToast: (message: string) => void }) {
  return (
    <div className="page-stack">
      <PageHeader eyebrow="Data" title="Static data, cached locally." />
      <section className="info-grid">
        <InfoTile label="Version" value={appData.manifest.dataVersion} />
        <InfoTile label="Updated" value={appData.manifest.updatedAt} />
        <InfoTile label="Next refresh" value={appData.manifest.nextPlannedUpdateAt} />
        <InfoTile label="Coverage" value={coverageLabel(appData.manifest.coverageStatus)} />
        <InfoTile label="Shop records" value={`${appData.shops.length}`} />
        <InfoTile label="Minimum guard" value={`${appData.manifest.expectedMinimumShopCount}`} />
        <InfoTile label="Stored locally" value="Shop and map cache only" />
        <InfoTile label="Free host target" value={appData.manifest.hostingUrl.replace("https://", "")} />
      </section>
      <section className="panel">
        <h2>Monthly refresh</h2>
        <p>
          The repository workflow refreshes public directory-map provider data on the first day of each
          month, validates the output, cleans older generated files, and opens a review pull request.
        </p>
      </section>
      <section className="panel">
        <h2>Sources</h2>
        <p>
          Primary source: {appData.manifest.sourceSnapshotUrl}. Review source:{" "}
          {appData.manifest.canonicalSourceUrl}.
        </p>
        {appData.manifest.sourceNotes.length > 0 && (
          <div className="source-note-list">
            {appData.manifest.sourceNotes.map((note) => (
              <span key={note}>{note}</span>
            ))}
          </div>
        )}
      </section>
      <section className="panel">
        <h2>Local cache</h2>
        <p>Clear cached shop and navigation data if the app feels stale.</p>
        <button
          className="secondary-button"
          onClick={() => {
            clearLocalData();
            setToast("Local cache cleared");
          }}
        >
          Clear local data
        </button>
      </section>
    </div>
  );
}

function IndoorMap({
  appData,
  navigationData,
  selectedFloorId,
  selectedShop,
  routeResult,
  navigate
}: {
  appData: AppData;
  navigationData: NavigationData;
  selectedFloorId: string;
  selectedShop: Shop | null;
  routeResult: RouteResult | null;
  navigate: (path: string) => void;
}) {
  const floor = navigationData.floors.find((item) => item.id === selectedFloorId);
  const shopsById = new Map(appData.shops.map((shop) => [shop.id, shop]));
  const floorShops = appData.shops.filter((shop) => floorIdFromShop(shop) === selectedFloorId).sort(compareShopByLot);
  const floorFacilities = appData.facilities.filter((facility) => normalizeFloorId(facility.floor) === selectedFloorId);
  const floorPoints = navigationData.shopPoints
    .filter((point) => point.floorId === selectedFloorId)
    .sort((a, b) => a.displayOrder - b.displayOrder);
  const floorLandmarks = navigationData.landmarks.filter((landmark) => landmark.floorId === selectedFloorId).slice(0, 42);
  const schematic = buildFloorSchematic(selectedFloorId, floorPoints, shopsById, floorLandmarks);
  const selectedPlottedShop = selectedShop ? schematic.shops.find((item) => item.shop.id === selectedShop.id) ?? null : null;
  const plottedShops = selectedPlottedShop
    ? [...schematic.shops.filter((item) => item.shop.id !== selectedPlottedShop.shop.id), selectedPlottedShop]
    : schematic.shops;
  const routeLine = selectedShop ? routePolyline(selectedPlottedShop, schematic, selectedFloorId) : null;
  const routeStart = selectedFloorId === "lg" ? schematic.start : schematic.liftStart;
  const floorWalkways = walkwayPaths(schematic);
  const isRouteMode = Boolean(selectedShop);

  return (
    <section className="map-panel enhanced-map-panel">
      <div className="map-toolbar">
        <div>
          <p className="eyebrow">{floor?.label ?? selectedFloorId}</p>
          <h2>{selectedShop ? selectedShop.name : "Indoor route map"}</h2>
        </div>
        <div className="map-toolbar-meta">
          <span className="start-badge">Start: LG Tower Two door</span>
          <span className="start-badge neutral">{floorShops.length} shops on floor</span>
        </div>
      </div>

      <div className={isRouteMode ? "map-layout route-only" : "map-layout"}>
        <div className="map-scroll" role="img" aria-label="IOI-layout-style indoor map">
          <svg
            className="indoor-map directory-map"
            width={schematic.width}
            height={schematic.height}
            viewBox={`0 0 ${schematic.width} ${schematic.height}`}
          >
            <defs>
              <pattern id={`grid-${selectedFloorId}`} width="38" height="38" patternUnits="userSpaceOnUse">
                <path d="M38 0 H0 V38" className="map-grid-line" />
              </pattern>
              <marker
                id={`route-arrow-${selectedFloorId}`}
                markerWidth="10"
                markerHeight="10"
                refX="8"
                refY="5"
                orient="auto"
                markerUnits="userSpaceOnUse"
              >
                <path d="M1 1.5 L8 5 L1 8.5 z" className="route-arrow" />
              </marker>
            </defs>
            <rect className="map-grid-bg" x="0" y="0" width={schematic.width} height={schematic.height} fill={`url(#grid-${selectedFloorId})`} />
            <rect className="directory-paper" x="28" y="28" width={schematic.width - 56} height={schematic.height - 56} rx="18" />
            <path
              className="mall-plan-shell"
              d={mallShellPath(schematic)}
            />
            <text className="floor-medallion" x="76" y="94">{floor?.shortLabel ?? selectedFloorId.toUpperCase()}</text>
            <path className="walkway-main" d={floorWalkways.main} />
            {floorWalkways.branches.map((path) => (
              <path key={path} className="walkway-branch" d={path} />
            ))}
            <g className="centre-walk-label">
              <rect x={schematic.centreWalkX - 54} y={schematic.walkwayY - 15} width="108" height="30" rx="15" />
              <text x={schematic.centreWalkX} y={schematic.walkwayY + 5}>Centre Walk</text>
            </g>

            {schematic.segments.map((segment) => (
              <g key={segment.wing} className={`map-zone-segment ${segment.wing}`}>
                <rect x={segment.startX} y="102" width={segment.endX - segment.startX} height="680" rx="10" />
                <line x1={segment.endX + 24} y1="130" x2={segment.endX + 24} y2="760" />
                <text x={segment.startX + 18} y="132">{segment.label}</text>
              </g>
            ))}

            {schematic.landmarks.map((landmark) => (
              <g key={landmark.id} className={`landmark ${landmark.type}`}>
                <circle cx={landmark.x} cy={landmark.y} r="15" />
                <text x={landmark.x} y={landmark.y + 4}>{landmarkSymbol(landmark.type)}</text>
                <title>{landmark.name}</title>
              </g>
            ))}

            {plottedShops.map(({ point, shop, box }) => {
              const selected = selectedShop?.id === shop.id;
              return (
                <g
                  key={point.shopId}
                  className={selected ? "shop-cell selected" : "shop-cell"}
                  style={mapStyle(shop.categoryIds[0])}
                  onClick={() => navigate(`/map/${point.floorId}/shop/${shop.slug}`)}
                >
                  <rect x={box.x} y={box.y} width={box.width} height={box.height} rx="2" />
                  <text className="lot-text primary-lot" x={box.x + box.width / 2} y={box.y + 20}>{mapLot(shop.locationCode)}</text>
                  <text className="shop-name-text" x={box.x + box.width / 2} y={box.y + 38}>{mapName(shop.name)}</text>
                  <title>{`${shop.name} · ${shop.locationCode}`}</title>
                </g>
              );
            })}

            {routeLine && (
              <>
                <polyline className="map-route-casing" points={routeLine.points} />
                <polyline className="map-route-path" points={routeLine.points} markerEnd={`url(#route-arrow-${selectedFloorId})`} />
                {routeLine.coords.slice(1, -1).map(([x, y], index) => (
                  <circle key={`${x}-${y}-${index}`} className="route-turn-dot" cx={x} cy={y} r="6" />
                ))}
              </>
            )}

            <g className="start-marker">
              <circle cx={routeStart.x} cy={routeStart.y} r="18" />
              <text x={routeStart.x + 28} y={routeStart.y + 5}>
                {selectedFloorId === "lg" ? "LG Tower Two door" : "Lift/escalator from LG"}
              </text>
            </g>
          </svg>
        </div>

        {!isRouteMode && <aside className="map-inspector">
          <p className="eyebrow">{selectedShop ? "Selected shop" : "Floor overview"}</p>
          <h3>{floor?.shortLabel ?? selectedFloorId}</h3>
          <p>
            Map cells show lot number and shop name only. Full details are grouped by category below.
          </p>
        </aside>}
      </div>

      <div className="map-legend">
        <span><i className="legend-line route" /> route</span>
        <span><i className="legend-line corridor" /> corridor</span>
        <span><i className="legend-dot store" /> shop cell</span>
        <span><i className="legend-dot" /> landmark</span>
      </div>

      {!isRouteMode && (
        <FloorLotGrid
          floorId={selectedFloorId}
          shops={floorShops}
          facilities={floorFacilities}
          categories={appData.categories}
          selectedShop={selectedShop}
          navigate={navigate}
        />
      )}
    </section>
  );
}

function FloorLotGrid({
  floorId,
  shops,
  facilities,
  categories,
  selectedShop,
  navigate
}: {
  floorId: string;
  shops: Shop[];
  facilities: Facility[];
  categories: Category[];
  selectedShop: Shop | null;
  navigate: (path: string) => void;
}) {
  type DirectoryBoardItem =
    | {
        kind: "shop";
        id: string;
        name: string;
        locationCode: string;
        categoryIds: string[];
        wing: string | null;
        detail: string;
        sortKey: string;
        shop: Shop;
      }
    | {
        kind: "facility";
        id: string;
        name: string;
        locationCode: string;
        categoryIds: string[];
        wing: string | null;
        detail: string;
        sortKey: string;
      };
  const [activeCategory, setActiveCategory] = useState("all");
  const directoryItems: DirectoryBoardItem[] = [
    ...shops.map((shop) => ({
      kind: "shop" as const,
      id: shop.id,
      name: shop.name,
      locationCode: shop.locationCode,
      categoryIds: shop.categoryIds,
      wing: shop.wing,
      detail: `${categoryName(shop.categoryIds[0] ?? "services", categories)} · ${shop.navigationDataQuality}`,
      sortKey: shop.lotSortKey,
      shop
    })),
    ...facilities.map((facility, index) => ({
      kind: "facility" as const,
      id: facility.id,
      name: facility.name,
      locationCode: `${facility.floor} facility`,
      categoryIds: facility.categoryIds.length > 0 ? facility.categoryIds : ["facilities"],
      wing: facility.wing,
      detail: `${readableType(facility.type)} · ${wingLabel(facility.wing)}`,
      sortKey: `${normalizeFloorId(facility.floor)}-${facility.wing ?? "centre"}-facility-${String(index).padStart(3, "0")}`
    }))
  ].sort((a, b) => a.sortKey.localeCompare(b.sortKey) || a.name.localeCompare(b.name));
  const presentCategoryIds = Array.from(new Set(directoryItems.flatMap((item) => item.categoryIds)))
    .sort((a, b) => {
      const left = categories.find((category) => category.id === a)?.sortOrder ?? 999;
      const right = categories.find((category) => category.id === b)?.sortOrder ?? 999;
      return left - right || a.localeCompare(b);
    });
  const visibleCategoryIds = activeCategory === "all" ? presentCategoryIds : presentCategoryIds.filter((id) => id === activeCategory);
  const sections = visibleCategoryIds
    .map((categoryId) => ({
      categoryId,
      category: categories.find((category) => category.id === categoryId),
      items: directoryItems.filter((item) => item.categoryIds.includes(categoryId))
    }))
    .filter((section) => section.items.length > 0);

  if (directoryItems.length === 0) {
    return (
      <div className="floor-directory-board empty">
        <h3>Category directory</h3>
        <p>No loaded shop or facility records for {floorId.toUpperCase()} yet.</p>
      </div>
    );
  }

  return (
    <div className="floor-directory-board">
      <div className="section-header">
        <div>
          <p className="eyebrow">Details below the map</p>
          <h3>{shops.length} shops and {facilities.length} facilities on this floor</h3>
        </div>
      </div>
      <div className="directory-filters" aria-label="Directory category filters">
        <button className={activeCategory === "all" ? "active" : ""} onClick={() => setActiveCategory("all")}>
          All
        </button>
        {presentCategoryIds.map((categoryId) => (
          <button
            key={categoryId}
            className={activeCategory === categoryId ? "active" : ""}
            style={mapStyle(categoryId)}
            onClick={() => setActiveCategory(categoryId)}
          >
            {categoryName(categoryId, categories)}
          </button>
        ))}
      </div>
      <div className="directory-lanes">
        {sections.map((section) => (
          <section key={section.categoryId} className="lot-lane" style={mapStyle(section.categoryId)}>
            <div className="lane-title">
              <span>{section.category?.name ?? section.categoryId}</span>
              <strong>{section.items.length}</strong>
            </div>
            <div className="lot-card-grid">
              {section.items.map((item) =>
                item.kind === "shop" ? (
                  <button
                    key={`${section.categoryId}-${item.id}`}
                    className={selectedShop?.id === item.id ? "lot-card active" : "lot-card"}
                    style={mapStyle(item.categoryIds[0])}
                    onClick={() => navigate(`/map/${floorId}/shop/${item.shop.slug}`)}
                  >
                    <span className="lot-code">{item.locationCode}</span>
                    <strong>{item.name}</strong>
                    <small>{item.detail}</small>
                    <small>{wingLabel(item.wing)}</small>
                  </button>
                ) : (
                  <div key={`${section.categoryId}-${item.id}`} className="lot-card facility-card" style={mapStyle(item.categoryIds[0])}>
                    <span className="lot-code">{item.locationCode}</span>
                    <strong>{item.name}</strong>
                    <small>{item.detail}</small>
                  </div>
                )
              )}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

function FloorTabs({
  floors,
  currentFloorId,
  navigate
}: {
  floors: NavigationData["floors"];
  currentFloorId: string;
  navigate: (path: string) => void;
}) {
  return (
    <div className="floor-tabs" role="tablist" aria-label="Floors">
      {floors.map((floor) => (
        <button
          key={floor.id}
          className={floor.id === currentFloorId ? "active" : ""}
          onClick={() => navigate(`/map/${floor.id}`)}
        >
          {floor.shortLabel}
        </button>
      ))}
    </div>
  );
}

function ShopList({
  shops,
  categories,
  navigate
}: {
  shops: Shop[];
  categories: Category[];
  navigate: (path: string) => void;
}) {
  return (
    <div className="shop-list">
      {shops.map((shop) => (
        <button key={shop.id} className="shop-card" onClick={() => navigate(`/shop/${shop.slug}`)}>
          <span>
            <strong>{shop.name}</strong>
            <small>{shop.locationCode} · {shop.categoryIds.map((id) => categoryName(id, categories)).join(", ")}</small>
          </span>
          <span className={`quality ${shop.navigationDataQuality}`}>{shop.navigationDataQuality}</span>
        </button>
      ))}
    </div>
  );
}

function PageHeader({ eyebrow, title }: { eyebrow: string; title: string }) {
  return (
    <section className="page-header">
      <p className="eyebrow">{eyebrow}</p>
      <h1>{title}</h1>
    </section>
  );
}

function InfoTile({ label, value }: { label: string; value: string }) {
  return (
    <div className="info-tile">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function EmptyState({
  title,
  message,
  actionLabel,
  onAction
}: {
  title: string;
  message: string;
  actionLabel?: string;
  onAction?: () => void;
}) {
  return (
    <section className="panel state-panel">
      <h2>{title}</h2>
      <p>{message}</p>
      {actionLabel && onAction && (
        <button className="primary-button" onClick={onAction}>
          {actionLabel}
        </button>
      )}
    </section>
  );
}

function Select({
  label,
  value,
  options,
  onChange
}: {
  label: string;
  value: string;
  options: Array<[string, string]>;
  onChange: (value: string) => void;
}) {
  return (
    <label className="select-field">
      <span>{label}</span>
      <select value={value} onChange={(event) => onChange(event.target.value)}>
        {options.map(([optionValue, optionLabel]) => (
          <option key={optionValue} value={optionValue}>
            {optionLabel}
          </option>
        ))}
      </select>
    </label>
  );
}

function IssueReportModal({
  appData,
  draft,
  routePath,
  onClose,
  onCopied
}: {
  appData: AppData;
  draft: IssueDraft;
  routePath: string;
  onClose: () => void;
  onCopied: () => void;
}) {
  const [type, setType] = useState<IssueType>(draft.defaultType);
  const [note, setNote] = useState("");
  const report = formatIssueReport({
    type,
    note,
    routePath,
    shop: draft.shop,
    routeResult: draft.routeResult,
    appData,
    floorId: draft.floorId,
    screenSize: `${window.innerWidth}x${window.innerHeight}`
  });

  async function copyReport() {
    await navigator.clipboard.writeText(report);
    onCopied();
    onClose();
  }

  return (
    <div className="modal-backdrop" role="presentation">
      <section className="issue-modal" role="dialog" aria-modal="true" aria-labelledby="issue-title">
        <div className="section-header">
          <div>
            <p className="eyebrow">Report issue</p>
            <h2 id="issue-title">Copy a troubleshooting report</h2>
          </div>
          <button className="ghost-button" onClick={onClose} aria-label="Close issue report">
            Close
          </button>
        </div>

        <Select
          label="Issue type"
          value={type}
          onChange={(value) => setType(value as IssueType)}
          options={[
            ["Wrong shop info", "Wrong shop info"],
            ["Wrong lot/location", "Wrong lot/location"],
            ["Navigation instruction unclear", "Navigation instruction unclear"],
            ["Missing shop", "Missing shop"],
            ["App error", "App error"],
            ["Other", "Other"]
          ]}
        />

        <label className="textarea-field">
          <span>Note</span>
          <textarea value={note} onChange={(event) => setNote(event.target.value)} rows={4} />
        </label>

        <pre className="issue-preview">{report}</pre>

        <div className="detail-actions">
          <button className="primary-button" onClick={copyReport}>
            Copy report
          </button>
          <button className="secondary-button" onClick={onClose}>
            Cancel
          </button>
        </div>
      </section>
    </div>
  );
}
